import React, { useEffect, useState } from 'react';
import { Form, Row, Col } from '@/components/ui';
import FiltersCard from '../../../components/FiltersCard';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import { areasService } from '../../../services/areasService';

export interface VendorFilterState {
  statuses: string[];
  serviceAreaIds: number[];
  complianceIssuesOnly: boolean;
}

export const EMPTY_VENDOR_FILTERS: VendorFilterState = {
  statuses: [],
  serviceAreaIds: [],
  complianceIssuesOnly: false,
};

interface Props {
  filters: VendorFilterState;
  onFilterChange: (filters: VendorFilterState) => void;
  onClearFilters: () => void;
}

const STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pending approval' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const VendorsFilters: React.FC<Props> = ({ filters, onFilterChange, onClearFilters }) => {
  const [serviceAreas, setServiceAreas] = useState<Array<{ id: number; name: string }>>([]);

  useEffect(() => {
    areasService
      .getServiceAreas()
      .then((areas: any[]) => setServiceAreas(areas ?? []))
      .catch(() => setServiceAreas([]));
  }, []);

  const set = <K extends keyof VendorFilterState>(field: K, value: VendorFilterState[K]) =>
    onFilterChange({ ...filters, [field]: value });

  const hasActiveFilters =
    !!filters.statuses.length || !!filters.serviceAreaIds.length || filters.complianceIssuesOnly;

  return (
    <FiltersCard
      title="Vendor Filters"
      subtitle="Filter by status, service area, and compliance"
      showClearButton
      onClear={onClearFilters}
      hasActiveFilters={hasActiveFilters}
    >
      <Row>
        <Col md={3}>
          <MultiSelectDropdown
            options={STATUS_OPTIONS}
            value={filters.statuses}
            onChange={(v) => set('statuses', v.map(String))}
            getValue={(o) => o.value}
            formatOption={(o) => o.label}
            placeholder="All Statuses"
            label="Status"
            className="tw-w-full"
            showSearch={false}
            // Lifecycle order (pending -> active -> inactive) reads better than alphabetical.
            preserveOptionsOrder
          />
        </Col>
        <Col md={3}>
          <MultiSelectDropdown
            options={serviceAreas}
            value={filters.serviceAreaIds}
            onChange={(v) => set('serviceAreaIds', v.map(Number))}
            getValue={(o) => o.id}
            formatOption={(o) => o.name}
            placeholder="All Service Areas"
            label="Service Area"
            className="tw-w-full"
          />
        </Col>
        <Col md={6}>
          <Form.Group>
            {/*
              HE-M18 is an indicator, not a gate — but a coordinator chasing lapsed
              certificates needs to find them, and scanning every row for a triangle does not
              scale. This narrows to exactly those, without blocking anything.
            */}
            <Form.Label>Compliance</Form.Label>
            <Form.Check
              type="checkbox"
              id="compliance-issues-only"
              label="Only vendors with expired or missing WCB / liability insurance"
              checked={filters.complianceIssuesOnly}
              onChange={(e) => set('complianceIssuesOnly', e.target.checked)}
            />
          </Form.Group>
        </Col>
      </Row>
    </FiltersCard>
  );
};

export default VendorsFilters;
